import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface BrowserNotificationSettings {
  /** Master switch — reflects the user's intent to receive OS pop-ups. */
  enabled: boolean;
  /** Global pop-up for every new headline. */
  headlines: boolean;
  /** Global pop-up for every new Truth Social story. */
  truthSocial: boolean;
}

export const DEFAULT_BROWSER_NOTIFICATION_SETTINGS: BrowserNotificationSettings =
  {
    enabled: false,
    headlines: false,
    truthSocial: false,
  };

interface BrowserNotificationStore {
  settings: BrowserNotificationSettings;
  updateSettings: (patch: Partial<BrowserNotificationSettings>) => void;
}

/**
 * Global browser-notification toggles. A shared store (not a per-instance hook)
 * so every consumer — the settings UI, the watchlist editors, and the firehose/
 * headlines ingest paths — sees the same value the instant it changes within a
 * tab. (A localStorage hook only syncs across *other* tabs via the `storage`
 * event, which left same-tab consumers stale.) Persisted locally because OS
 * notification permission is per-device/per-browser.
 */
const useBrowserNotificationStore = create<BrowserNotificationStore>()(
  persist(
    (set) => ({
      settings: DEFAULT_BROWSER_NOTIFICATION_SETTINGS,
      updateSettings: (patch) =>
        set((state) => ({ settings: { ...state.settings, ...patch } })),
    }),
    {
      name: "browser.notification.settings",
    }
  )
);

export function useBrowserNotificationSettings() {
  const settings = useBrowserNotificationStore((s) => s.settings);
  const updateSettings = useBrowserNotificationStore((s) => s.updateSettings);
  return { settings, updateSettings };
}
